import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { FileTypeDatasetAdapter, formatAdapts, compressionAdapts } from './file-type.dataset.adapter';
import { AmazonS3Dataset } from 'app/model/dataset/amazon-s3.dataset';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Owned } from 'app/model/base/named';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AmazonS3Dataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            structure: '*',
            linkedServiceName: '*',
            parameters: '*',
            type: '*',
            typeProperties: {
                bucketName: '*',
                key: '*',
                prefix: '*',
                version: '*',
                modifiedDatetimeStart: '*',
                modifiedDatetimeEnd: '*',
                wildcardPath: '*',
                format: formatAdapts,
                compression: compressionAdapts
            }
        }
    }
})
export class AmazonS3DatasetAdapter extends FileTypeDatasetAdapter<AmazonS3Dataset, Model.DatasetResource> {

    protected async _toResource(dataset: AmazonS3Dataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.AmazonS3Dataset;
        modelDataset.type = Metamodel.modelFor(AmazonS3Dataset).name;
        modelDataset.structure = this.columnValueToResource(dataset, dataset.columns);
        modelDataset.typeProperties = {
            ...this.formatAndCompressionValueToResource(dataset.format, dataset.compression),
            key: ValueAdapter.toResource(dataset, AmazonS3Dataset.FILE_NAME),
            prefix: ValueAdapter.toResource(dataset, AmazonS3Dataset.FOLDER_PATH),
            bucketName: ValueAdapter.toResource(dataset, AmazonS3Dataset.BUCKET_NAME),
            wildcardPath: ValueAdapter.toResource(dataset, AmazonS3Dataset.WILDCARD_PATH),
            modifiedDatetimeStart: ValueAdapter.toResource(dataset, AmazonS3Dataset.MODIFID_DATETIME_START),
            modifiedDatetimeEnd: ValueAdapter.toResource(dataset, AmazonS3Dataset.MODIFID_DATETIME_END)
        };
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetResource, dataset?: AmazonS3Dataset): Promise<AmazonS3Dataset> {
        if (!dataset) {
            dataset = AmazonS3Dataset.MODEL.create<AmazonS3Dataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        const model = modelDatasetResource.properties as Model.AmazonS3Dataset;

        if (model) {
            if (model.typeProperties != null) {
                Owned.own(dataset, 'format', this.formatResourceToValue(model.typeProperties.format));
                dataset.compression = this.compressionResourceToValue(model.typeProperties.compression);
                ValueAdapter.toModel(dataset, AmazonS3Dataset.BUCKET_NAME, model.typeProperties.bucketName);
                ValueAdapter.toModel(dataset, AmazonS3Dataset.WILDCARD_PATH, model.typeProperties.wildcardPath);
                ValueAdapter.toModel(dataset, AmazonS3Dataset.FILE_NAME, model.typeProperties.key);
                ValueAdapter.toModel(dataset, AmazonS3Dataset.FOLDER_PATH, model.typeProperties.prefix);
                ValueAdapter.toModel(dataset, AmazonS3Dataset.MODIFID_DATETIME_START, model.typeProperties.modifiedDatetimeStart);
                ValueAdapter.toModel(dataset, AmazonS3Dataset.MODIFID_DATETIME_END, model.typeProperties.modifiedDatetimeEnd);
            } else {
                this.initializeModel(dataset);
            }
            dataset.columns = this.columnResourceToValue(dataset, model.structure);
        }
        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, AmazonS3Dataset.MODEL));
        return dataset;
    }
}
